/**
 * SEO Component - Dynamic Meta Tags & Structured Data
 * For modul-spesifikk SEO-optimalisering
 * @version 2.0.0 - Oppdatert for profesjonelt økosystem
 */

import { useEffect } from 'react';

// Base URL for OG images
const OG_IMAGE_BASE = 'https://rettverk.no';
const DEFAULT_OG_IMAGE = `${OG_IMAGE_BASE}/og-image-rettverk.png`;

// SEO configurations for each module
export const SEO_CONFIG = {
  // Landing/Home - Ny profesjonell tittel
  home: {
    title: 'RettVerk – Juridisk plattform for bedrifter og privatpersoner',
    description: 'Profesjonell juridisk plattform med AI-drevet assistanse. Prøv gratis i 30 dager. Arbeidsrett, husleierett, GDPR, HMS og mer – for bedrifter og privatpersoner.',
    keywords: 'juridisk plattform, arbeidsrett, husleierett, GDPR, HMS, forbrukerrett, barnevern, AI juridisk, bedriftsløsning, juridisk assistent',
    ogImage: DEFAULT_OG_IMAGE,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'RettVerk - Juridisk plattform',
      description: 'Profesjonell juridisk plattform for bedrifter og privatpersoner'
    }
  },
  
  // B2B Landing - Optimert for bedriftsmarkedet
  bedrift: {
    title: 'RettVerk Bedrift – GDPR, HMS, Arbeidsrett & Vault for virksomheter',
    description: 'Komplett juridisk plattform for bedrifter. GDPR-compliance, HMS-dokumentasjon, arbeidsrett-moduler og sikker Vault-lagring. Prøv gratis i 30 dager. Skalerbar løsning for alle bransjer.',
    keywords: 'GDPR bedrift, HMS programvare, arbeidsrett bedrift, juridisk plattform bedrift, Vault sikker lagring, compliance software, B2B juridisk, personvern løsning, internkontroll',
    canonical: '/bedrift',
    ogImage: DEFAULT_OG_IMAGE,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'RettVerk Bedrift',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: 'Juridisk plattform for bedrifter med GDPR, HMS og Vault',
      offers: {
        '@type': 'Offer',
        price: '0',
        priceCurrency: 'NOK',
        description: 'Prøv gratis i 30 dager'
      }
    }
  },
  
  // Husleierett Module
  husleierett: {
    title: 'Husleierett – Leiekontrakt, Depositum & Vedlikeholdsansvar | RettVerk',
    description: 'Lag profesjonelle leiekontrakter, analyser avtaler med AI og forstå vedlikeholdsansvar. Prøv gratis i 30 dager. Komplett verktøykasse basert på Husleieloven.',
    keywords: 'husleierett, leiekontrakt, depositum, vedlikeholdsansvar, husleieloven, utleier, leietaker, oppsigelse leieforhold, mangel bolig, fremleie',
    canonical: '/husleierett',
    ogImage: DEFAULT_OG_IMAGE,
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Guide',
      name: 'Husleierett',
      description: 'Komplett guide til husleierett med AI-analyse og dokumentgeneratorer',
      about: {
        '@type': 'Thing',
        name: 'Husleieloven'
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'Leietakere og utleiere'
      }
    }
  },
  
  // Arbeidsrett Module
  arbeidsrett: {
    title: 'Arbeidsrett – Oppsigelse, Sluttavtale & Arbeidskontrakt | RettVerk',
    description: 'Analyser arbeidskontrakter, forstå oppsigelsesregler og beregn sluttavtaler. Prøv gratis i 30 dager. AI-drevet juridisk hjelp basert på Arbeidsmiljøloven.',
    keywords: 'arbeidsrett, oppsigelse, sluttavtale, arbeidskontrakt, arbeidsmiljøloven, permittering, avskjed, arbeidsforhold, stillingsvern',
    canonical: '/arbeidsrett',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Guide',
      name: 'Arbeidsrett',
      description: 'Komplett guide til arbeidsrett med kontraktanalyse og oppsigelseskalkulator',
      about: {
        '@type': 'Thing',
        name: 'Arbeidsmiljøloven'
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'Arbeidstakere og arbeidsgivere'
      }
    }
  },
  
  // Forbrukerrett Module
  forbrukerrett: {
    title: 'Forbrukerrett – Reklamasjon, Klageveiviser & Kjøpsrett | RettVerk',
    description: 'Generer profesjonelle klagebrev, forstå reklamasjonsfrister og få hjelp med FKU-saker. Prøv gratis i 30 dager. Komplett verktøykasse etter Forbrukerkjøpsloven.',
    keywords: 'forbrukerrett, reklamasjon, klage, forbrukerkjøpsloven, FKU, forbrukerklagenemnda, kjøpsrett, garanti, heving, prisavslag',
    canonical: '/forbrukerrett',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Guide',
      name: 'Forbrukerrett',
      description: 'Komplett guide til forbrukerrett med klagegenerator og FKU-eksport',
      about: {
        '@type': 'Thing',
        name: 'Forbrukerkjøpsloven'
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'Forbrukere'
      }
    }
  },
  
  // Barnevern Module
  barnevern: {
    title: 'Barnevern – Juridisk Veiledning & Dokumenthjelp | RettVerk',
    description: 'Forstå barnevernssaker, få veiledning om rettigheter og plikter. Prøv gratis i 30 dager. AI-assistert juridisk hjelp for familier i kontakt med barnevernet.',
    keywords: 'barnevern, barnevernssak, rettigheter foreldre, barnevernloven, omsorgsovertakelse, samvær, fylkesnemnd, dokumenthjelp',
    canonical: '/barnevern',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Guide',
      name: 'Barnevern',
      description: 'Juridisk veiledning og dokumenthjelp for barnevernssaker',
      about: {
        '@type': 'Thing',
        name: 'Barnevernloven'
      },
      audience: {
        '@type': 'Audience',
        audienceType: 'Foreldre og pårørende'
      }
    }
  },
  
  // Chat/AI Module
  chat: {
    title: 'Juridisk Chat – Spør om dine rettigheter og få umiddelbare svar | RettVerk',
    description: 'Chat med vår AI-drevne juridiske assistent. Prøv gratis i 30 dager. Få umiddelbare svar på spørsmål om husleie, arbeid, forbrukerrett og barnevern.',
    keywords: 'juridisk chat, AI assistent, juridisk hjelp, spør jurist, rettigheter, juridisk rådgivning, gratis juridisk hjelp',
    canonical: '/chat',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebApplication',
      name: 'RettVerk Juridisk Chat',
      description: 'AI-drevet juridisk assistent for norsk lov',
      applicationCategory: 'LegalApplication',
      operatingSystem: 'Web'
    }
  },
  
  // Documents Module
  documents: {
    title: 'Dokumentbibliotek – Dine juridiske dokumenter | RettVerk',
    description: 'Administrer alle dine juridiske dokumenter på ett sted. Trygg lokal lagring med kryptering. Eksporter til PDF, Word eller direkte til myndigheter.',
    keywords: 'juridiske dokumenter, dokumentbibliotek, klagebrev, kontrakter, lokal lagring, kryptering',
    canonical: '/documents',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'Dataset',
      name: 'RettVerk Dokumentbibliotek',
      description: 'Personlig bibliotek for juridiske dokumenter'
    }
  },
  
  // System Admin
  systemAdmin: {
    title: 'System Administrator – RettVerk Kontrollpanel',
    description: 'System Administrator dashboard for RettVerk-plattformen. Kundeadministrasjon, MRR-oversikt og systemstatus.',
    keywords: 'admin, system admin, kontrollpanel, dashboard',
    canonical: '/system-admin',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'RettVerk System Admin',
      description: 'Administrator kontrollpanel'
    }
  }
};

/**
 * Hook for setting document meta tags
 */
export function usePageSEO(moduleKey) {
  useEffect(() => {
    const config = SEO_CONFIG[moduleKey];
    if (!config) return;
    
    // Update title
    document.title = config.title;
    
    // Update meta description
    const descMeta = document.querySelector('meta[name="description"]');
    if (descMeta) {
      descMeta.setAttribute('content', config.description);
    }
    
    // Update meta keywords
    const keywordsMeta = document.querySelector('meta[name="keywords"]');
    if (keywordsMeta) {
      keywordsMeta.setAttribute('content', config.keywords);
    }
    
    // Update OG tags
    const ogTitle = document.querySelector('meta[property="og:title"]');
    if (ogTitle) {
      ogTitle.setAttribute('content', config.title);
    }
    
    const ogDesc = document.querySelector('meta[property="og:description"]');
    if (ogDesc) {
      ogDesc.setAttribute('content', config.description);
    }
    
    // Update Twitter tags
    const twTitle = document.querySelector('meta[name="twitter:title"]');
    if (twTitle) {
      twTitle.setAttribute('content', config.title);
    }
    
    const twDesc = document.querySelector('meta[name="twitter:description"]');
    if (twDesc) {
      twDesc.setAttribute('content', config.description);
    }
    
    // Add/Update canonical link
    let canonical = document.querySelector('link[rel="canonical"]');
    if (config.canonical) {
      if (!canonical) {
        canonical = document.createElement('link');
        canonical.setAttribute('rel', 'canonical');
        document.head.appendChild(canonical);
      }
      canonical.setAttribute('href', `https://rettverk.no${config.canonical}`);
    }
    
    // Add structured data
    if (config.schema) {
      // Remove existing module-specific schema
      const existingSchema = document.getElementById('module-schema');
      if (existingSchema) {
        existingSchema.remove();
      }
      
      // Add new schema
      const script = document.createElement('script');
      script.id = 'module-schema';
      script.type = 'application/ld+json';
      script.textContent = JSON.stringify(config.schema);
      document.head.appendChild(script);
    }
    
    // Cleanup on unmount
    return () => {
      // Reset to default title on page change
      // (Optional: could reset to home config)
    };
  }, [moduleKey]);
}

/**
 * Structured Data for Breadcrumbs
 */
export function generateBreadcrumbSchema(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `https://rettverk.no${item.path}`
    }))
  };
}

/**
 * Structured Data for FAQ
 */
export function generateFAQSchema(faqs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer
      }
    }))
  };
}

export default usePageSEO;
